import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Chip,
  Divider,
  Alert,
  Box,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import WorkIcon from '@mui/icons-material/Work';
import ViewListIcon from '@mui/icons-material/ViewList';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import PersonGraph from '../../components/PersonGraph/PersonGraph';

export interface DirectorshipItem {
  companyId: string;
  companyName: string;
  roleTitle?: string;
  isNominee?: boolean;
}

interface PersonDirectorSectionProps {
  directorships: DirectorshipItem[];
  view: 'list' | 'graph';
  onViewChange: (view: 'list' | 'graph') => void;
  personId: string | undefined;
  showViewToggle?: boolean;
}

const PersonDirectorSection: React.FC<PersonDirectorSectionProps> = ({
  directorships,
  view,
  onViewChange,
  personId,
  showViewToggle = false,
}) => (
  <Box>
    {showViewToggle && (
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Directorships ({directorships.length})</Typography>
        <ToggleButtonGroup
          value={view}
          exclusive
          onChange={(_, newView) => newView && onViewChange(newView)}
          size="small"
        >
          <ToggleButton value="list">
            <ViewListIcon fontSize="small" sx={{ mr: 1 }} />
            List
          </ToggleButton>
          <ToggleButton value="graph">
            <AccountTreeIcon fontSize="small" sx={{ mr: 1 }} />
            Graph
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>
    )}

    {view === 'graph' && personId ? (
      <PersonGraph personId={personId} view="directorships" height="700px" />
    ) : (
      <Card>
        <CardContent>
          {!showViewToggle && (
            <>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WorkIcon color="primary" />
                Directorships
                <Chip label={directorships.length} size="small" color="primary" />
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </>
          )}
          {directorships.length === 0 ? (
            <Alert severity="info">No directorships found.</Alert>
          ) : (
            <List disablePadding={!showViewToggle}>
              {directorships.map((d, idx) => (
                <React.Fragment key={d.companyId}>
                  {idx > 0 && <Divider />}
                  <ListItemButton
                    component={RouterLink}
                    to={`/companies/${encodeURIComponent(d.companyId)}`}
                    sx={showViewToggle ? {} : { px: 0 }}
                  >
                    <ListItemText
                      primary={
                        <Typography component="span" variant="body2" fontWeight="medium">
                          {d.companyName}
                        </Typography>
                      }
                      secondary={
                        <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                          <Chip
                            label={d.roleTitle || 'Director'}
                            size="small"
                            variant="outlined"
                          />
                          {d.isNominee && (
                            <Chip
                              label="Nominee"
                              size="small"
                              color="warning"
                              variant="outlined"
                            />
                          )}
                        </Stack>
                      }
                      secondaryTypographyProps={{ component: 'div' }}
                    />
                  </ListItemButton>
                </React.Fragment>
              ))}
            </List>
          )}
        </CardContent>
      </Card>
    )}
  </Box>
);

export default PersonDirectorSection;
